/*
 * Cortazu: inline "Notify me" for a sold-out size on the PDP (markup: snippets/cz-notify-me.liquid).
 *
 * - Shows itself when the selected variant is sold out, hides when it is available or the
 *   combination does not exist. Listens to FoxTheme's variantChange pubsub event, which Hyper
 *   publishes both on the optimistic update and after the section render.
 * - Replaces the "Sold out" label on the disabled add-to-cart with "Choose an available size"
 *   while other sizes are still in stock.
 * - Submits to Klaviyo's client Back in Stock endpoint (public company id, no private key).
 * - Suggests the same size in other colours (Cortazu swatches, else Rubik Combined Listings siblings) when in stock.
 * - q14 (16 Oct): sits below the add-to-cart; an outline button "Notify me when L is back" opens the email form.
 * - 19 Oct (owner): when the shopper picks a sold-out size (size picker or the sticky bar's size sheet), the email
 *   form opens right away with the field focused; no extra click on "Notify me when L is back". A sold-out first
 *   paint (e.g. a ?variant= link) keeps the button, so the page does not jump on load.
 * - Products without a size option (data-has-size="false", e.g. a single default variant) get the
 *   generic texts: "Sold out" (or "<variant> is sold out"), no size relabel, no alternatives.
 */
(() => {
  if (customElements.get('cz-notify-me')) return;

  const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
  const KLAVIYO_REVISION = '2024-06-15';
  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const MAX_ALTERNATIVES = 3;

  // Sibling product JSON is the same for every box on the page: fetch each handle once.
  const siblingCache = new Map();

  const format = (template, values) =>
    template.replace(/\[(\w+)\]/g, (match, key) => (key in values ? values[key] : match));

  const rootUrl = () => {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  class CzNotifyMe extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.hasSize = this.dataset.hasSize !== 'false';
      this.variants = this.readJson('[data-cz-notify-variants]') || {};
      this.strings = this.readJson('[data-cz-notify-strings]') || {};
      this.form = this.querySelector('[data-cz-notify-form]');
      this.input = this.form.querySelector('input[type="email"]');
      this.submitButton = this.form.querySelector('[data-cz-notify-submit]');
      this.submitLabel = this.querySelector('[data-cz-notify-label]');
      this.defaultLabel = this.submitLabel.textContent;
      this.message = this.querySelector('[data-cz-notify-message]');
      this.heading = this.querySelector('[data-cz-notify-heading]');
      this.alternatives = this.querySelector('[data-cz-notify-alternatives]');
      this.alternativesList = this.querySelector('[data-cz-notify-alternatives-list]');
      this.toggle = this.querySelector('[data-cz-notify-toggle]');
      this.panel = this.querySelector('[data-cz-notify-panel]');
      this.subscribed = new Set();

      this.onToggle = () => this.open();
      // A size picked by the shopper (not the first paint): remember when, so the variant change that follows opens the form.
      this.onPick = (event) => {
        const input = event.target;
        if (!input || !input.matches || !input.matches('input[type="radio"]')) return;
        if (!input.closest('.product__block--variant_picker, variant-selects')) return;
        const scope = this.closest('product-info') || document;
        if (!scope.contains(input)) return;
        this.pickedAt = Date.now();
      };
      document.addEventListener('change', this.onPick, true);
      this.toggle?.addEventListener('click', this.onToggle);

      this.onSubmit = this.onSubmit.bind(this);
      this.onInput = () => this.clearError();
      this.form.addEventListener('submit', this.onSubmit);
      this.input.addEventListener('input', this.onInput);

      const subscribe = () => {
        if (!window.FoxTheme || !FoxTheme.pubsub) return false;
        this.unsubscribe = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, (event) =>
          this.onVariantChange(event)
        );
        return true;
      };
      if (!subscribe()) document.addEventListener('DOMContentLoaded', subscribe, { once: true });

      this.update(this.dataset.variantId);
    }

    disconnectedCallback() {
      this.unsubscribe?.();
      clearTimeout(this.swatchTimer);
      this.form?.removeEventListener('submit', this.onSubmit);
      this.toggle?.removeEventListener('click', this.onToggle);
      document.removeEventListener('change', this.onPick, true);
      this.input?.removeEventListener('input', this.onInput);
    }

    readJson(selector) {
      try {
        return JSON.parse(this.querySelector(selector).textContent);
      } catch (error) {
        return null;
      }
    }

    onVariantChange({ data } = {}) {
      if (!data || data.sectionId !== this.sectionId) return;
      this.update(data.variant ? String(data.variant.id) : null, data.variant);
    }

    get hasStock() {
      return Object.values(this.variants).some((variant) => variant.available);
    }

    open(auto) {
      if (!this.panel) return;
      this.panel.hidden = false;
      this.toggle.setAttribute('aria-expanded', 'true');
      if (!auto) {
        this.input.focus();
        return;
      }
      // Opened by a size pick: focus without a jump, then bring the field into view when it is not.
      // Hyper re-renders the picker after the pick and puts focus back on the size (which also stops a smooth
      // scroll); take focus back and scroll again during the next moments.
      const show = () => {
        this.input.focus({ preventScroll: true });
        const rect = this.input.getBoundingClientRect();
        const header = document.querySelector('.cz-hdr header, header')?.getBoundingClientRect().bottom || 0;
        if (rect.top < Math.max(header, 0) || rect.bottom > window.innerHeight - 16) {
          this.input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      };
      show();
      const until = Date.now() + 3000;
      const refocus = (event) => {
        if (Date.now() > until) return document.removeEventListener('focusin', refocus, true);
        if (event.target !== this.input && event.target.matches?.('input[type="radio"], label, body')) setTimeout(show, 0);
      };
      document.addEventListener('focusin', refocus, true);
      // Hyper scrolls the gallery to the variant's photo after its re-render (page jumps up on desktop): while the
      // shopper has not scrolled or typed themselves, bring the field back once that settles.
      let userMoved = false;
      const moved = () => (userMoved = true);
      const stopEvents = ['wheel', 'touchstart', 'keydown', 'pointerdown'];
      stopEvents.forEach((type) => window.addEventListener(type, moved, { passive: true, once: true }));
      let settle;
      const onScroll = () => {
        clearTimeout(settle);
        settle = setTimeout(() => {
          if (!userMoved && !this.panel.hidden && Date.now() < until) show();
        }, 200);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
      setTimeout(() => {
        document.removeEventListener('focusin', refocus, true);
        window.removeEventListener('scroll', onScroll);
        stopEvents.forEach((type) => window.removeEventListener(type, moved));
        const active = document.activeElement;
        if (!userMoved && !this.panel.hidden && (!active || active === document.body || active === this.input || active.matches('input[type="radio"]'))) show();
      }, 3000);
    }

    collapse() {
      if (!this.panel) return;
      this.panel.hidden = true;
      this.toggle.setAttribute('aria-expanded', 'false');
    }

    get swatches() {
      return this.closest('product-info')?.querySelector('.cz-sw--pdp') || null;
    }

    get colour() {
      const own = this.swatches?.querySelector('.cz-sw__label-value');
      if (own) return own.textContent.trim();
      // Rubik Combined Listings renders the colour swatches in an open shadow root.
      const listing = this.closest('product-info')?.querySelector('rubik-combined-listings');
      const value = listing?.shadowRoot?.querySelector('.rcl__group-label-selected-value');
      return value ? value.textContent.trim() : '';
    }

    update(variantId, variantData) {
      const known = variantId ? this.variants[variantId] : null;
      const available = variantData ? !!variantData.available : !!known?.available;

      if (!variantId || available || !known) {
        this.hidden = true;
        this.variantId = variantId;
        return;
      }

      let changed = false;
      if (this.variantId !== variantId) {
        changed = true;
        this.variantId = variantId;
        this.size = known.size;
        this.resetState();
        this.renderHeading();
        this.renderAlternatives();
        if (this.hasSize && !this.colour) this.waitForSwatches();
      }
      this.hidden = false;
      this.relabelAddButton();
      // 19 Oct: the shopper just picked this sold-out size -> email field straight away (not after a success).
      if (changed && this.pickedAt && Date.now() - this.pickedAt < 4000 && this.dataset.state !== 'success') {
        this.pickedAt = 0;
        this.open(true);
      }
    }

    // Rubik renders its swatches after page load: on a first paint that is already sold out,
    // fill in the colour and the alternatives once they exist (checks for up to ~5 seconds).
    waitForSwatches(attempt = 0) {
      clearTimeout(this.swatchTimer);
      if (attempt > 10) return;
      this.swatchTimer = setTimeout(() => {
        if (this.hidden) return;
        if (!this.colour) return this.waitForSwatches(attempt + 1);
        this.renderHeading();
        this.renderAlternatives();
      }, 500);
    }

    renderHeading() {
      if (this.toggle) {
        this.toggle.textContent =
          this.size && this.strings.toggle
            ? format(this.strings.toggle, { size: this.size })
            : this.strings.toggle_default || this.toggle.textContent;
      }
      if (!this.size) {
        this.heading.textContent = this.strings.heading_default || '';
        return;
      }
      const colour = this.hasSize ? this.colour : '';
      this.heading.textContent = colour
        ? format(this.strings.heading, { size: this.size, colour })
        : format(this.strings.heading_no_colour, { size: this.size });
    }

    relabelAddButton() {
      // Hyper writes "Sold out" on every variant change; only relabel while other sizes are in stock.
      if (!this.hasSize || !this.hasStock || !this.strings.choose_available) return;
      const label = document.querySelector(`#ProductSubmitButton-${this.sectionId} .btn__text`);
      if (label) label.textContent = this.strings.choose_available;
    }

    resetState() {
      this.dataset.state = '';
      this.form.hidden = false;
      this.collapse();
      this.message.textContent = '';
      this.input.removeAttribute('aria-invalid');
      this.setBusy(false);
      if (this.subscribed.has(this.variantId)) this.showSuccess();
    }

    clearError() {
      if (this.dataset.state !== 'error') return;
      this.dataset.state = '';
      this.input.removeAttribute('aria-invalid');
      this.message.textContent = '';
    }

    showError(text) {
      this.dataset.state = 'error';
      this.input.setAttribute('aria-invalid', 'true');
      this.message.textContent = text;
    }

    showSuccess() {
      this.dataset.state = 'success';
      if (this.panel) this.panel.hidden = false;
      this.form.hidden = true;
      this.message.textContent =
        this.hasSize && this.size
          ? format(this.strings.success, { size: this.size })
          : this.strings.success_generic || format(this.strings.success, { size: this.size });
    }

    setBusy(busy) {
      this.submitButton.disabled = busy;
      this.submitButton.setAttribute('aria-busy', String(busy));
      this.submitLabel.textContent = busy ? this.strings.sending || this.defaultLabel : this.defaultLabel;
    }

    async onSubmit(event) {
      event.preventDefault();
      const email = this.input.value.trim();

      if (!email) {
        this.showError(this.strings.error_empty);
        this.input.focus();
        return;
      }
      if (!EMAIL_PATTERN.test(email)) {
        this.showError(this.strings.error_invalid);
        this.input.focus();
        return;
      }

      const variantId = this.variantId;
      this.clearError();
      this.setBusy(true);

      try {
        const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(this.dataset.companyId)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            accept: 'application/json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify({
            data: {
              type: 'back-in-stock-subscription',
              attributes: {
                profile: { data: { type: 'profile', attributes: { email } } },
                channels: ['EMAIL']
              },
              relationships: {
                variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } }
              }
            }
          })
        });

        if (!response.ok) throw new Error(`Klaviyo responded ${response.status}`);

        this.subscribed.add(variantId);
        this.setBusy(false);
        // The shopper may have switched size while the request was in flight.
        if (this.variantId === variantId) this.showSuccess();
      } catch (error) {
        console.warn('[cz-notify] Back in stock subscription failed:', error);
        this.setBusy(false);
        if (this.variantId === variantId) this.showError(this.strings.error_generic);
      }
    }

    async renderAlternatives() {
      this.alternatives.hidden = true;
      this.alternativesList.textContent = '';
      if (!this.hasSize || !this.size) return;

      // Cortazu swatches (cz-swatches.liquid) when switched on, else Rubik's shadow-root swatches.
      const own = Array.from(this.swatches?.querySelectorAll('a.cz-sw__dot[href]') || [])
        .filter((dot) => !dot.classList.contains('is-current') && !dot.classList.contains('is-sold-out'))
        .map((dot) => ({ href: dot.getAttribute('href'), colour: dot.getAttribute('aria-label') || '' }));
      const listing = this.closest('product-info')?.querySelector('rubik-combined-listings');
      const rubik = Array.from(listing?.shadowRoot?.querySelectorAll('a.rcl__swatch[href]') || [])
        .filter(
          (swatch) =>
            !swatch.classList.contains('rcl__swatch--active') && !swatch.classList.contains('rcl__swatch--unavailable')
        )
        .map((swatch) => ({
          href: swatch.getAttribute('href'),
          colour: swatch.dataset.optionValue || swatch.getAttribute('aria-label') || ''
        }));
      const swatches = own.length ? own : rubik;
      if (!swatches.length) return;

      const size = this.size;
      const variantId = this.variantId;
      const results = await Promise.all(
        swatches.map(async (swatch) => {
          const handle = (swatch.href.match(/\/products\/([^/?#]+)/) || [])[1];
          if (!handle) return null;
          const product = await this.fetchSibling(handle);
          const sizeIndex = product ? this.sizeIndex(product) : -1;
          const match = product?.variants?.find((variant) => variant.available && variant.options[sizeIndex] === size);
          if (!match) return null;
          return {
            colour: swatch.colour,
            url: `${rootUrl()}products/${handle}?variant=${match.id}`
          };
        })
      );

      // A newer selection owns the box by now.
      if (this.variantId !== variantId) return;

      const found = results.filter(Boolean).slice(0, MAX_ALTERNATIVES);
      if (!found.length) return;

      found.forEach((alternative, index) => {
        if (index > 0) this.alternativesList.append(', ');
        const link = document.createElement('a');
        link.href = alternative.url;
        link.className = 'cz-notify__alternative';
        link.textContent = format(this.strings.alternative, { size, colour: alternative.colour });
        this.alternativesList.append(link);
      });
      this.alternatives.hidden = false;
    }

    sizeIndex(product) {
      const names = (product.options || []).map((option) => String(option.name || option).toLowerCase());
      const index = names.findIndex((name) => ['size', 'maat', 'größe', 'grösse', 'taille'].includes(name));
      return index === -1 ? 0 : index;
    }

    fetchSibling(handle) {
      if (!siblingCache.has(handle)) {
        siblingCache.set(
          handle,
          fetch(`${rootUrl()}products/${handle}.js`)
            .then((response) => (response.ok ? response.json() : null))
            .catch(() => null)
        );
      }
      return siblingCache.get(handle);
    }
  }

  customElements.define('cz-notify-me', CzNotifyMe);
})();
